// Réinitialisation du mot de passe du COMPTE DE SECOURS local (bris de glace) — deux temps sur une
// seule page : demander le lien (sans jeton dans l'URL), puis poser le mot de passe (avec jeton).
//
// ⚠️ POURQUOI CET ÉCRAN EXISTE : le serveur montait déjà `/v1/auth/password/{forgot,reset}` et la
// Console a bien un compte local (`db/seed.ts`), donc un e-mail de réinitialisation PARTAIT — mais
// aucune page ne recevait le lien, et le joker `<Route path="*">` renvoyait silencieusement vers `/`.
// Le jeton était consommé en pure perte et l'on retombait sur le login sans un mot d'explication :
// pire qu'un 404, qui aurait au moins dit que la page n'existait pas (§23.0).
// ⚠️ Les comptes FÉDÉRÉS ne passent pas par ici : leur mot de passe vit à l'IdP, qui a sa propre
// réinitialisation. Cette page ne concerne que le bris de glace.
import { useState, type FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../api';

export function Reset() {
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const token = sp.get('token') ?? '';
  const [email, setEmail] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  // Demande du lien. ⚠️ La route est NON-ÉNUMÉRANTE (200 même si l'adresse est inconnue) : l'écran dit
  // donc « si un compte existe », jamais « e-mail envoyé » — l'affirmer inventerait une certitude.
  const askLink = async (e: FormEvent) => {
    e.preventDefault();
    setErr(null); setBusy(true);
    try { await api.forgotPassword(email.trim().toLowerCase()); setSent(true); }
    catch (e) { setErr((e as Error).message); } finally { setBusy(false); }
  };

  const apply = async (e: FormEvent) => {
    e.preventDefault();
    setErr(null);
    if (next.length < 12) { setErr('Le mot de passe doit faire au moins 12 caractères.'); return; }
    if (next !== confirm) { setErr('La confirmation ne correspond pas.'); return; }
    setBusy(true);
    try { await api.resetPassword(token, next); nav('/login', { replace: true }); }
    catch (e) { setErr((e as Error).message); } finally { setBusy(false); }
  };

  return (
    <div className="authwrap">
      <form className="card" onSubmit={token ? apply : askLink}>
        <h1>Orgazone Console</h1>
        <p className="sub">{token ? 'Nouveau mot de passe' : 'Mot de passe oublié'}</p>

        {token ? (
          <>
            <label htmlFor="pwd">Nouveau mot de passe</label>
            <input id="pwd" type="password" autoComplete="new-password" value={next}
              onChange={(e) => { setNext(e.target.value); setErr(null); }} required autoFocus />
            <label htmlFor="pwd2">Confirmer</label>
            <input id="pwd2" type="password" autoComplete="new-password" value={confirm}
              onChange={(e) => { setConfirm(e.target.value); setErr(null); }} required />
            <button className="btn btn-cta block" type="submit" disabled={busy} style={{ marginTop: 16 }}>
              {busy ? '…' : 'Définir le mot de passe'}
            </button>
          </>
        ) : sent ? (
          <div className="banner">
            Demande enregistrée. <b>Si un compte local existe pour cette adresse</b>, un lien vient de lui
            être envoyé — il expire rapidement. Rien de plus n'est confirmé : cette page ne sait pas si
            l'adresse correspond à un compte.
          </div>
        ) : (
          <>
            <div className="banner">
              Réservé au <b>compte de secours local</b>. Un accès Orgazone (SSO) se réinitialise depuis
              l'IdP, pas ici.
            </div>
            <label htmlFor="email">E-mail</label>
            <input id="email" type="email" autoComplete="username" value={email}
              onChange={(e) => { setEmail(e.target.value); setErr(null); }} required autoFocus />
            <button className="btn btn-cta block" type="submit" disabled={busy} style={{ marginTop: 16 }}>
              {busy ? '…' : 'Recevoir un lien'}
            </button>
          </>
        )}

        {err && <div className="err">{err}</div>}
        <p className="note">
          <a href="/login" onClick={(e) => { e.preventDefault(); nav('/login'); }}>Retour à la connexion</a>
        </p>
      </form>
    </div>
  );
}
